"use client";

import { getProviders, signIn, getSession } from "next-auth/react";
import { useEffect, useState, Suspense } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import React from "react";
import config from "@/config";
import LoadingSpinner from "@/components/LoadingSpinner";
import { PeeqdLogoIcon } from "@/components/peeqd/icons";
import { trackFunnelEvent, storeSessionForRedirect } from "@/libs/ga";
import { REDIRECT_MESSAGES, showRedirectLoading } from "@/libs/redirectLoading";

interface Provider {
  id: string;
  name: string;
  type: string;
  signinUrl: string;
  callbackUrl: string;
}

const TRUST_SIGNALS = [
  {
    title: "Follower diffs",
    description: "See who followed, unfollowed, and who they started following.",
  },
  {
    title: "Story alerts",
    description: "Get notified the moment a tracked account posts a story.",
  },
  {
    title: "Activity reports",
    description: "Weekly summaries with real timestamps on every change.",
  },
] as const;

/**
 * Name given to the OAuth popup by {@link useGooglePopupAuth}. If we find
 * ourselves on /auth/signin inside a window with this name AND a valid
 * `window.opener`, we're in the popup flow - NextAuth redirected us here
 * because the OAuth roundtrip failed. Never show the full sign-in UI in
 * that case: post the error to the parent and close the popup.
 */
const GOOGLE_POPUP_WINDOW_NAME = "Google Sign In";

function SignInMarketingPanel() {
  return (
    <div className="relative hidden overflow-hidden bg-gradient-to-br from-[#EF5F7E] via-[#F0577C] to-[#8B5CF6] lg:flex lg:w-1/2">
      <div className="absolute inset-0 bg-black/15" />
      <div className="relative flex flex-1 items-center justify-center px-8 py-12">
        <div className="max-w-xl text-center text-white">
          <p className="text-xs font-semibold uppercase tracking-widest text-white/80">
            Instagram activity reports
          </p>
          <h3 className="mt-4 font-display text-4xl font-bold leading-tight">
            Track any account. Get the full picture.
          </h3>
          <p className="mt-4 text-lg text-white/90">{config.appDescription}</p>

          <div className="mt-10 space-y-4 text-left">
            {TRUST_SIGNALS.map((signal) => (
              <div
                key={signal.title}
                className="rounded-2xl border border-white/20 bg-white/10 p-5 backdrop-blur-md"
              >
                <div className="text-lg font-semibold">{signal.title}</div>
                <div className="mt-1 text-sm text-white/80">{signal.description}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function SignInContent() {
  const [providers, setProviders] = useState<Record<string, Provider> | null>(null);
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [isInPopupFlow, setIsInPopupFlow] = useState(false);
  const router = useRouter();
  const searchParams = useSearchParams();
  const callbackUrl = searchParams.get("callbackUrl") || "/";

  // If we're inside the Google popup (NextAuth bounced us here on failure),
  // report the error to the parent modal and close the window immediately
  // instead of rendering the fallback sign-in UI.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const opener = window.opener as Window | null;
    const inPopup = !!opener && window.name === GOOGLE_POPUP_WINDOW_NAME;
    if (!inPopup) return;

    setIsInPopupFlow(true);
    const errorCode = searchParams.get("error") || "Callback";
    try {
      opener.postMessage(
        {
          type: "googlePopupAuth",
          status: "error",
          error: errorCode,
        },
        window.location.origin
      );
    } catch {
      // Ignore - we still want to close the popup even if postMessage fails.
    }
    // Close after a tiny delay so the message is flushed before teardown.
    window.setTimeout(() => window.close(), 50);
  }, [searchParams]);

  useEffect(() => {
    if (isInPopupFlow) return;
    const setupProviders = async () => {
      const providersData = await getProviders();
      setProviders(providersData);
    };
    setupProviders();
  }, [isInPopupFlow]);

  // Track sign-in page view only once on mount
  useEffect(() => {
    trackFunnelEvent("acquisition", "signin_viewed", {
      callback_url: callbackUrl,
      referrer: document.referrer,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isInPopupFlow) return;
    const checkSession = async () => {
      const session = await getSession();
      if (session) {
        showRedirectLoading(REDIRECT_MESSAGES.redirecting);
        router.push(callbackUrl);
      }
    };
    checkSession();
  }, [router, callbackUrl, isInPopupFlow]);

  const handleGoogleSignIn = () => {
    storeSessionForRedirect("oauth");

    trackFunnelEvent("acquisition", "signin_started", {
      auth_method: "google",
      callback_url: callbackUrl,
    });

    showRedirectLoading(REDIRECT_MESSAGES.signingIn);
    signIn("google", { callbackUrl });
  };

  const handleEmailSignIn = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email) return;

    storeSessionForRedirect("oauth");

    trackFunnelEvent("acquisition", "signin_started", {
      auth_method: "email",
      callback_url: callbackUrl,
    });

    setIsLoading(true);
    try {
      await signIn("email", {
        email,
        callbackUrl,
        redirect: false,
      });
      setEmailSubmitted(true);

      trackFunnelEvent("acquisition", "email_sent", {
        auth_method: "email",
      });
    } catch (err) {
      console.error("Email sign in error:", err);

      trackFunnelEvent("acquisition", "signin_error", {
        auth_method: "email",
        error: err instanceof Error ? err.message : "Unknown error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Inside the Google popup after an error: render a blank placeholder while
  // we post the error to the parent and close ourselves. Never show the
  // fallback sign-in UI here - the user is supposed to see the parent modal.
  if (isInPopupFlow) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <LoadingSpinner size="md" text="" fullScreen={false} />
      </div>
    );
  }

  if (emailSubmitted) {
    return (
      <div className="flex min-h-screen">
        <SignInMarketingPanel />

        <div className="flex flex-1 items-center justify-center bg-gray-50 px-4 sm:px-6 lg:px-8">
          <div className="w-full max-w-md space-y-8">
            <div className="text-center">
              <h2 className="text-3xl font-bold text-gray-900">Check your email</h2>
              <p className="mt-4 text-gray-600">
                We&apos;ve sent a magic link to <strong>{email}</strong>
              </p>
              <p className="mt-2 text-sm text-gray-500">
                Click the link in the email to sign in to {config.appName}.
              </p>

              <div className="mt-4 flex justify-center">
                <div className="inline-flex items-center gap-2 rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-2">
                  <svg className="h-5 w-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                  <span className="text-sm font-medium text-yellow-800">Check your Spam folder</span>
                </div>
              </div>

              <button
                type="button"
                onClick={() => {
                  setEmailSubmitted(false);
                  setEmail("");
                }}
                className="mt-4 text-sm font-medium text-primary hover:text-primary/80"
              >
                ← Back to sign in
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen">
      <SignInMarketingPanel />

      <div className="flex flex-1 items-center justify-center bg-gray-50 px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-md space-y-8">
          <div className="text-center">
            <Link href="/" className="mb-6 inline-flex items-center justify-center gap-2">
              <PeeqdLogoIcon className="h-9 w-9 shadow-soft" />
              <span className="font-display text-xl font-extrabold text-gray-900">{config.appName}</span>
            </Link>
            <h2 className="text-3xl font-bold text-gray-900">Sign in</h2>
            <p className="mt-2 text-gray-600">
              Sign in or create an account to track Instagram accounts and unlock activity reports.
            </p>
          </div>

          <div className="space-y-4">
            {!providers && (
              <div className="flex items-center justify-center py-8">
                <LoadingSpinner size="sm" text="" fullScreen={false} />
              </div>
            )}

            {providers?.google && (
              <button
                type="button"
                onClick={handleGoogleSignIn}
                className="flex w-full items-center justify-center rounded-xl bg-gray-900 px-4 py-3 font-medium text-white transition-colors duration-200 hover:bg-gray-800"
              >
                <svg className="mr-3 h-5 w-5" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Continue with Google
              </button>
            )}

            {providers?.google && providers?.email && (
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="bg-gray-50 px-2 text-gray-500">or</span>
                </div>
              </div>
            )}

            {providers?.email && (
              <form onSubmit={handleEmailSignIn} className="space-y-4">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/30"
                />
                <button
                  type="submit"
                  disabled={isLoading || !email}
                  className="flex w-full items-center justify-center rounded-xl border border-gray-300 bg-white px-4 py-3 font-medium text-gray-700 transition-colors duration-200 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <svg className="mr-3 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                  {isLoading ? "Sending..." : "Continue with Email"}
                </button>
              </form>
            )}

            <p className="text-center text-xs text-gray-500">
              By continuing, you agree to our{" "}
              <Link href="/terms" target="_blank" className="text-primary hover:text-primary/80">
                Terms of Use
              </Link>{" "}
              and{" "}
              <Link href="/privacy" target="_blank" className="text-primary hover:text-primary/80">
                Privacy Policy
              </Link>
              .
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SignIn() {
  return (
    <Suspense
      fallback={
        <div className="flex min-h-screen items-center justify-center bg-gray-50">
          <LoadingSpinner size="md" text="" fullScreen={false} />
        </div>
      }
    >
      <SignInContent />
    </Suspense>
  );
}
